import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { Loader2 } from "lucide-react"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { VerUktRekap } from "@/types"
import { MdOutlineSync } from "react-icons/md"
import { Input } from "@/components/ui/input"
import { GiBackwardTime } from "react-icons/gi"

const postData = async (newData: {
  id_verukt_rekap: string
  total_peserta: number
  jumlah_kelompok: number
}) => {
  const response = await api.post(
    "/setting/verifikator-ukt/buat-kelompok",
    newData
  )
  return response.data
}

const deleteData = async ({ id_verukt_rekap }: { id_verukt_rekap: string }) => {
  const response = await api.delete("/setting/verifikator-ukt/reset-kelompok", {
    data: { id_verukt_rekap },
  })
  return response.data
}

export default function FormKelompok({
  closeModal,
  selectedRow,
  method,
}: {
  closeModal: () => void
  selectedRow: VerUktRekap | null
  method: "POST" | "DELETE"
}) {
  const [form, setForm] = useState({
    id_verukt_rekap: selectedRow?.id_verukt_rekap || "",
    total_peserta: selectedRow?.total_peserta || 0,
    jumlah_kelompok: 0,
  })

  const isPost = method === "POST"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isDelete ? deleteData : undefined,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listRekapBerkasUkt"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<GiBackwardTime />}
          title={"Reset Kelompok"}
          description={`Reset jumlah kelompok yang telah dibuat.`}
        />
        <div className="mt-3 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            onClick={() => closeModal()}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button className="flex items-center gap-3" disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Reset"
            )}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<MdOutlineSync />}
        title={"Buat Kelompok"}
        description={`Dari ${form.total_peserta} peserta akan dibagi jumlah kelompok dan hasilnya adalah jumlah peserta yang akan diverifikasi oleh verifikator`}
      />
      <div>
        <Label>Total Peserta</Label>
        <Input readOnly disabled value={form.total_peserta} />
      </div>

      <div>
        <Label>Jumlah Kelompok</Label>
        <Input
          placeholder="Ketik jumlah kelompok (contoh: 10)"
          value={form.jumlah_kelompok === 0 ? "" : form.jumlah_kelompok}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              jumlah_kelompok: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="mt-3 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          onClick={() => closeModal()}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  )
}
